import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, Sparkles, SlidersHorizontal, X, Save, Hash, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { MatchCard } from "@/components/MatchCard";
import { SkeletonCard, Empty } from "@/components/States";
import { toast } from "sonner";

const SORTS = [["recommended", "Recommended"], ["compatibility", "Compatibility"], ["recently_active", "Recently Active"], ["new", "Newest Profiles"], ["completion", "Profile Completion"]];
const QUICK = [["photo_available", "Photo Available"], ["verified", "Verified"]];

export default function Search() {
  const nav = useNavigate();
  const [filters, setFilters] = useState({ age_min: "", age_max: "", city: "", state: "", religion: "", occupation: "", qualification: "", diet: "", mother_tongue: "" });
  const [sort, setSort] = useState("recommended");
  const [showFilters, setShowFilters] = useState(false);
  const [state, setState] = useState({ loading: false, results: [], total: 0, pages: 1, page: 1 });
  const [aiQuery, setAiQuery] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [pid, setPid] = useState("");
  const [saved, setSaved] = useState([]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v !== false));

  const run = async (page = 1, f = filters, s = sort) => {
    setState((st) => ({ ...st, loading: true }));
    try {
      const { data } = await api.post(`/search?page=${page}&sort=${s}`, clean(f));
      setState({ loading: false, results: data.results, total: data.total, pages: data.pages, page });
    } catch (e) { toast.error(apiError(e)); setState((st) => ({ ...st, loading: false })); }
  };

  useEffect(() => { run(1); api.get("/saved-searches").then(({ data }) => setSaved(data.searches)).catch(() => {}); }, []);

  const toggleQuick = (k) => { const nf = { ...filters, [k]: !filters[k] }; setFilters(nf); run(1, nf); };
  const reset = () => { const nf = { age_min: "", age_max: "", city: "", state: "", religion: "", occupation: "", qualification: "", diet: "", mother_tongue: "" }; setFilters(nf); run(1, nf); };

  const aiSearch = async () => {
    if (!aiQuery.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/search", { query: aiQuery });
      setState({ loading: false, results: data.results, total: data.total, pages: 1, page: 1 });
      toast.success("AI understood your request");
    } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
  };

  const findById = async () => {
    if (!pid.trim()) return;
    try {
      const { data } = await api.get(`/search/profile-id/${pid.trim()}`);
      nav(`/app/profile/${data.user_id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveCurrent = async () => {
    const name = window.prompt("Name this search:", "My Search");
    if (!name) return;
    try {
      const { data } = await api.post("/saved-searches", { name, filters: clean(filters) });
      setSaved((s) => [data, ...s]);
      toast.success("Search saved — you'll be alerted on new matches");
    } catch (e) { toast.error(apiError(e)); }
  };

  const applySaved = (s) => { setFilters({ ...filters, ...s.filters }); run(1, { ...filters, ...s.filters }); };

  return (
    <div className="space-y-5" data-testid="search-page">
      <div>
        <h1 className="font-serif text-3xl font-bold text-[#58111A]">Search</h1>
        <p className="text-sm text-[#605255]">Find profiles with filters, AI search or Profile ID.</p>
      </div>

      {/* AI + Profile ID */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="mj-card p-4">
          <label className="mj-label flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#C59B27]" /> AI Search</label>
          <div className="flex gap-2">
            <input data-testid="ai-search-input" className="mj-input" placeholder='e.g. "software professional in Bangalore, 27-31, open to relocation"' value={aiQuery} onChange={(e) => setAiQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && aiSearch()} />
            <button data-testid="ai-search-btn" onClick={aiSearch} disabled={aiBusy} className="btn-primary shrink-0">{aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask AI"}</button>
          </div>
        </div>
        <div className="mj-card p-4">
          <label className="mj-label flex items-center gap-1.5"><Hash className="w-4 h-4 text-[#C59B27]" /> Search by Profile ID</label>
          <div className="flex gap-2">
            <input data-testid="pid-input" className="mj-input" placeholder="MJ-482731" value={pid} onChange={(e) => setPid(e.target.value)} onKeyDown={(e) => e.key === "Enter" && findById()} />
            <button data-testid="pid-btn" onClick={findById} className="btn-outline shrink-0">Open</button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters" className="btn-outline text-sm"><SlidersHorizontal className="w-4 h-4" /> Filters</button>
        {QUICK.map(([k, l]) => (
          <button key={k} onClick={() => toggleQuick(k)} data-testid={`quick-${k}`} className={`chip ${filters[k] ? "!bg-[#781826] !text-white !border-[#781826]" : ""}`}>{l}</button>
        ))}
        <select value={sort} onChange={(e) => { setSort(e.target.value); run(1, filters, e.target.value); }} data-testid="sort-select" className="mj-input !w-auto text-sm py-1.5">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={reset} data-testid="reset-filters" className="text-sm text-[#605255] hover:text-[#BE123C] flex items-center gap-1"><X className="w-3.5 h-3.5" /> Clear</button>
        <button onClick={saveCurrent} data-testid="save-search" className="text-sm text-[#781826] font-semibold flex items-center gap-1 ml-auto"><Save className="w-3.5 h-3.5" /> Save Search</button>
      </div>

      {saved.length > 0 && (
        <div className="flex gap-2 overflow-x-auto hide-scrollbar">
          {saved.map((s) => <button key={s.id} onClick={() => applySaved(s)} className="chip shrink-0" data-testid={`saved-${s.id}`}>{s.name}</button>)}
        </div>
      )}

      {showFilters && (
        <div className="mj-card p-5 grid grid-cols-2 md:grid-cols-3 gap-3 animate-fadeup" data-testid="filter-panel">
          <Field label="Age Min"><input className="mj-input" type="number" value={filters.age_min} onChange={(e) => set("age_min", e.target.value)} /></Field>
          <Field label="Age Max"><input className="mj-input" type="number" value={filters.age_max} onChange={(e) => set("age_max", e.target.value)} /></Field>
          <Field label="City"><input className="mj-input" value={filters.city} onChange={(e) => set("city", e.target.value)} /></Field>
          <Field label="State"><input className="mj-input" value={filters.state} onChange={(e) => set("state", e.target.value)} /></Field>
          <Field label="Religion"><input className="mj-input" value={filters.religion} onChange={(e) => set("religion", e.target.value)} /></Field>
          <Field label="Mother Tongue"><input className="mj-input" value={filters.mother_tongue} onChange={(e) => set("mother_tongue", e.target.value)} /></Field>
          <Field label="Profession"><input className="mj-input" value={filters.occupation} onChange={(e) => set("occupation", e.target.value)} /></Field>
          <Field label="Education"><input className="mj-input" value={filters.qualification} onChange={(e) => set("qualification", e.target.value)} /></Field>
          <Field label="Diet">
            <select className="mj-input" value={filters.diet} onChange={(e) => set("diet", e.target.value)}>
              <option value="">Any</option><option>Vegetarian</option><option>Non-Vegetarian</option><option>Eggetarian</option><option>Vegan</option>
            </select>
          </Field>
          <div className="col-span-2 md:col-span-3"><button onClick={() => run(1)} data-testid="apply-filters" className="btn-primary w-full">Apply Filters</button></div>
        </div>
      )}

      <p className="text-sm text-[#605255]">{state.total} profiles found</p>

      {state.loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}</div>
      ) : state.results.length === 0 ? (
        <Empty icon={SearchIcon} title="No profiles match" message="Try adjusting or clearing your filters, or use AI Search to describe what you're looking for." action={<button onClick={reset} className="btn-primary">Clear Filters</button>} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{state.results.map((p) => <MatchCard key={p.user_id} p={p} />)}</div>
          {state.pages > 1 && (
            <div className="flex justify-center gap-2 pt-4">
              {[...Array(state.pages)].slice(0, 8).map((_, i) => (
                <button key={i} onClick={() => run(i + 1)} className={`w-9 h-9 rounded-lg text-sm font-medium ${state.page === i + 1 ? "bg-[#781826] text-white" : "bg-white border border-[#E8DFD1] text-[#605255]"}`}>{i + 1}</button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="mj-label">{label}</label>{children}</div>;
}
